import {
  AimOutlined,
  BarChartOutlined,
  BarsOutlined,
  CalendarOutlined,
  ClearOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
  FilterOutlined,
  LeftOutlined,
  MedicineBoxOutlined,
  PlusOutlined,
  ProjectOutlined,
  RightOutlined,
  SwapOutlined,
  TableOutlined,
  TeamOutlined,
  UserAddOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  List,
  Modal,
  Progress,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AllocationModal, { type AllocationDefaults } from '../../components/ResourcePlanning/AllocationModal';
import GanttView, { type GanttGroup } from '../../components/ResourcePlanning/GanttView';
import TechnicianSwapModal from '../../components/ResourcePlanning/TechnicianSwapModal';
import LoadView from '../../components/ResourcePlanning/LoadView';
import OverloadDrawer from '../../components/ResourcePlanning/OverloadDrawer';
import {
  EMPTY_FILTERS,
  type Filters,
  period,
  type Range,
  shift,
  usePlanningModel,
} from '../../components/ResourcePlanning/planningModel';
import PlanningTable, { type CellTarget } from '../../components/ResourcePlanning/PlanningTable';
import SelectionDrawer from '../../components/ResourcePlanning/SelectionDrawer';
import {
  DATE,
  DISPLAY_DATE,
  gridQuery,
  homeTeam,
  hours,
  IconButton,
  planningTeamId,
  projectLabel,
  timeText,
  UNASSIGNED,
  useCanEdit,
  useMe,
  UserSelect,
  useProjects,
  useSystems,
  useTeams,
  utilizationStatus,
} from '../../components/ResourcePlanning/shared';
import TechnicianDayDrawer from '../../components/ResourcePlanning/TechnicianDayDrawer';
import type { Allocation, Lookup, Team } from '../../DTOS/ResourcePlanning';

type View = 'table' | 'gantt' | 'load';
const iconOption = <T extends string>(value: T, title: string, icon: React.ReactNode) => ({
  value,
  label: (
    <Space size={4}>
      {icon}
      {title}
    </Space>
  ),
});

const RANGES: Range[] = ['day', 'week', 'fourWeeks', 'month', 'quarter'],
  VIEWS: View[] = ['table', 'gantt', 'load'];
const ids = (value: string | null) => (value ? value.split(',').filter(Boolean) : []);
/** Links from the overview open this tab on a date, period, view and filters, e.g. ?tab=planning&date=2026-10-26&range=week&teams=<id>. */
const fromParams = (params: URLSearchParams): Filters => ({
  ...EMPTY_FILTERS,
  teams: ids(params.get('teams')),
  projects: ids(params.get('projects')),
  people: ids(params.get('people')),
  systems: ids(params.get('systems')),
  overloadedOnly: params.get('overloads') === '1',
  hideWeekends: params.get('weekends') === '0',
});

/** People added by hand appear in the plan even without a team or work in the period, so they can be allocated from the grid. */
const PeoplePicker: React.FC<{
  value: string[];
  people: Lookup[];
  teams: Team[];
  date: string;
  onChange: (value: string[]) => void;
  onClose: () => void;
}> = ({ value, people, teams, date, onChange, onClose }) => (
  <Modal open title='Tehnicieni adaugati in planificare' onCancel={onClose} footer={null} destroyOnHidden>
    <Space direction='vertical' style={{ width: '100%' }}>
      <Typography.Text type='secondary'>
        Orice utilizator activ poate fi planificat, cu sau fara echipa. Persoanele fara echipa apar in grupul „Fara echipa
        / alocari directe”; apartenenta la echipe nu se modifica.
      </Typography.Text>
      {/* Remounted after every pick so the search box is empty again. */}
      <UserSelect
        key={value.length}
        placeholder='Cautati si adaugati un tehnician'
        describe={(userId) => homeTeam(teams, userId, date)?.name || 'Fara echipa'}
        onChange={(userId?: string) => userId && !value.includes(userId) && onChange([...value, userId])}
      />
      <List
        size='small'
        dataSource={value}
        locale={{ emptyText: 'Niciun tehnician adaugat.' }}
        renderItem={(userId) => (
          <List.Item
            actions={[
              <Button key='remove' size='small' type='link' danger onClick={() => onChange(value.filter((id) => id !== userId))}>
                Elimina
              </Button>,
            ]}
          >
            {people.find((person) => person.id === userId)?.name || 'Se incarca...'}
            <Typography.Text type='secondary' style={{ marginLeft: 8, fontSize: 12 }}>
              {homeTeam(teams, userId, date)?.name || 'Fara echipa'}
            </Typography.Text>
          </List.Item>
        )}
      />
    </Space>
  </Modal>
);

const PlanningTab: React.FC = () => {
  const me = useMe();
  const canEdit = useCanEdit();
  const [params, setParams] = useSearchParams();
  const anchor = params.get('date') && dayjs(params.get('date')).isValid() ? dayjs(params.get('date')) : dayjs();
  const range: Range = RANGES.includes(params.get('range') as Range) ? (params.get('range') as Range) : 'week';
  const view: View = VIEWS.includes(params.get('view') as View) ? (params.get('view') as View) : 'table';
  const ganttGroup: GanttGroup = params.get('group') === 'project' ? 'project' : 'team';
  const split = params.get('daily') !== '0';
  const addedParam = params.get('add') || '';
  const added = useMemo(() => ids(addedParam), [addedParam]);
  // Keyed on the query string: a new object every render would rebuild the whole planning model.
  const search = params.toString();
  const filters = useMemo(() => fromParams(new URLSearchParams(search)), [search]);
  const updateParams = (patch: Record<string, string | undefined>) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.set('tab', 'planning');
      Object.entries(patch).forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)));
      return next;
    });
  const setAnchor = (date: dayjs.Dayjs) => updateParams({ date: date.format(DATE) });
  const setRange = (value: Range) => updateParams({ range: value });
  const setView = (value: View) => updateParams({ view: value });
  const setGanttGroup = (value: GanttGroup) => updateParams({ group: value });
  const setAdded = (value: string[]) => updateParams({ add: value.join(',') });
  const setFilters = (value: Filters) =>
    updateParams({
      teams: value.teams.join(','),
      projects: value.projects.join(','),
      people: value.people.join(','),
      systems: value.systems.join(','),
      overloads: value.overloadedOnly ? '1' : undefined,
      weekends: value.hideWeekends ? '0' : undefined,
    });
  const [editing, setEditing] = useState<{ allocation: Allocation | null; defaults?: AllocationDefaults } | null>(null);
  // Selected allocation ids; the panel always shows the current rows so versions stay fresh after each save.
  const [details, setDetails] = useState<string[] | null>(null);
  const [fixing, setFixing] = useState(false);
  const [day, setDay] = useState<CellTarget | null>(null);
  const [swap, setSwap] = useState<{ userId?: string; name?: string } | null>(null);
  const [picking, setPicking] = useState(false);
  const { data: teams = [] } = useTeams();
  const { data: projects = [] } = useProjects();
  const { data: systems = [] } = useSystems();
  const { first, last } = period(anchor, range);
  const from = first.format(DATE),
    to = last.format(DATE);
  useEffect(() => {
    setDay(null);
    setDetails(null);
  }, [from, to]);
  const { data, isLoading, error } = useQuery(gridQuery(from, to, added));
  const model = usePlanningModel(data, teams, projects, filters, first, last, added);
  const selected = details ? (data?.allocations || []).filter((allocation) => details.includes(allocation.id)) : [];

  const update = (patch: Partial<Filters>) => setFilters({ ...filters, ...patch });
  const filtered = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);
  const create = (target?: CellTarget) =>
    setEditing({
      allocation: null,
      defaults: target && {
        teamId: planningTeamId(target.teamId),
        userId: target.userId,
        projectId: target.projectId,
        from: target.date,
        to: target.to || target.date,
        minutes: target.minutes,
      },
    });
  const openDay = (target: CellTarget) => {
    setDetails(null);
    setDay({ ...target, teamId: planningTeamId(target.teamId) });
  };
  const editDay = (allocation: Allocation) =>
    openDay({ teamId: allocation.teamId, userId: allocation.userId, date: allocation.date });
  const viewProps = {
    model,
    loading: isLoading,
    onDay: openDay,
    onDetails: (allocations: Allocation[]) => {
      // A team lead edits several days at once only for their own members; other work opens read-only per day.
      const editable = allocations.filter((row) => canEdit(row.userId, row.date));
      if (
        allocations.length &&
        allocations.every((row) => row.userId === allocations[0].userId && row.date === allocations[0].date)
      )
        editDay(allocations[0]);
      else if (editable.length) setDetails(editable.map((allocation) => allocation.id));
      else if (allocations.length) editDay(allocations[0]);
    },
    onCreate: create,
    // Swapping two technicians' work or home teams is a planner action.
    onSwap: me.canPlan
      ? (person: { userId: string; name: string }) => setSwap({ userId: person.userId, name: person.name })
      : undefined,
  };
  const utilization = model.capacity ? Math.round((model.allocated * 100) / model.capacity) : 0;
  const kpis = [
    { title: 'Ore alocate', icon: <ClockCircleOutlined />, value: +(model.allocated / 60).toFixed(1), suffix: 'h' },
    { title: 'Capacitate', icon: <CalendarOutlined />, value: +(model.capacity / 60).toFixed(1), suffix: 'h' },
    { title: 'Persoane', icon: <TeamOutlined />, value: model.peopleCount },
    { title: 'Proiecte', icon: <ProjectOutlined />, value: model.projectCount },
    {
      title: 'Depasiri',
      icon: <WarningOutlined />,
      value: model.warnings.length,
      color: model.warnings.length ? '#cf1322' : undefined,
    },
  ];

  return (
    <Space direction='vertical' size='middle' style={{ width: '100%' }}>
      <Flex wrap gap={8} align='center' justify='space-between'>
        <Space wrap>
          <Segmented<Range>
            value={range}
            onChange={setRange}
            options={[
              { value: 'day', label: 'Zi' },
              { value: 'week', label: 'Saptamana' },
              { value: 'fourWeeks', label: '4 saptamani' },
              { value: 'month', label: 'Luna' },
              { value: 'quarter', label: 'Trimestru' },
            ]}
          />
          <Space.Compact>
            <IconButton
              title='Perioada anterioara'
              icon={<LeftOutlined />}
              onClick={() => setAnchor(shift(anchor, range, -1))}
            />
            <DatePicker
              value={anchor}
              onChange={(date) => date && setAnchor(date)}
              allowClear={false}
              format={DISPLAY_DATE}
            />
            <IconButton
              title='Perioada urmatoare'
              icon={<RightOutlined />}
              onClick={() => setAnchor(shift(anchor, range, 1))}
            />
          </Space.Compact>
          <IconButton title='Azi' icon={<AimOutlined />} onClick={() => setAnchor(dayjs())} />
          <Typography.Text type='secondary'>
            {first.format(DISPLAY_DATE)}
            {from !== to && ` – ${last.format(DISPLAY_DATE)}`}
          </Typography.Text>
        </Space>
        <Space wrap>
          <Segmented<View>
            value={view}
            onChange={setView}
            options={[
              iconOption('table', 'Tabel', <TableOutlined />),
              iconOption('gantt', 'Grafic Gantt', <BarsOutlined />),
              iconOption('load', 'Grad de incarcare', <BarChartOutlined />),
            ]}
          />
          {view === 'gantt' && (
            <Segmented<GanttGroup>
              value={ganttGroup}
              onChange={setGanttGroup}
              options={[
                iconOption('team', 'Grupare pe echipe', <TeamOutlined />),
                iconOption('project', 'Grupare pe proiecte', <ProjectOutlined />),
              ]}
            />
          )}
          {view === 'gantt' && (
            <Segmented<'daily' | 'bars'>
              value={split ? 'daily' : 'bars'}
              onChange={(value) => updateParams({ daily: value === 'bars' ? '0' : undefined })}
              options={[
                iconOption('daily', 'Ore pe zi', <TableOutlined />),
                iconOption('bars', 'Bare', <BarsOutlined />),
              ]}
            />
          )}
          {me.canPlan && (
            <IconButton title='Schimb intre doi tehnicieni' icon={<SwapOutlined />} onClick={() => setSwap({})} />
          )}
          <IconButton
            title='Adauga tehnicieni in planificare (inclusiv fara echipa)'
            icon={<UserAddOutlined />}
            type={added.length ? 'primary' : 'default'}
            ghost={added.length > 0}
            onClick={() => setPicking(true)}
          />
          <Button type='primary' icon={<PlusOutlined />} onClick={() => create()}>
            Alocare noua
          </Button>
        </Space>
      </Flex>

      <Flex wrap gap={8} align='center'>
        <Tooltip title='Filtre'>
          <FilterOutlined style={{ color: filtered ? '#1677ff' : '#8c8c8c' }} />
        </Tooltip>
        <Select
          mode='multiple'
          allowClear
          maxTagCount='responsive'
          value={filters.teams}
          onChange={(teamIds) => update({ teams: teamIds })}
          placeholder='Echipe'
          style={{ width: 200 }}
          options={[
            ...teams.map((team) => ({ value: team.id, label: team.name })),
            { value: UNASSIGNED, label: 'Fara echipa / alocari directe' },
          ]}
        />
        <Select
          mode='multiple'
          allowClear
          showSearch
          optionFilterProp='label'
          maxTagCount='responsive'
          value={filters.projects}
          onChange={(projectIds) => update({ projects: projectIds })}
          placeholder='Proiecte'
          style={{ width: 320 }}
          options={projects.map((project) => ({ value: project.id, label: projectLabel(project) }))}
        />
        <Select
          mode='multiple'
          allowClear
          showSearch
          optionFilterProp='label'
          maxTagCount='responsive'
          value={filters.people}
          onChange={(userIds) => update({ people: userIds })}
          placeholder='Persoane'
          style={{ width: 220 }}
          options={(data?.people || []).map((person) => ({ value: person.id, label: person.name }))}
        />
        <Select
          mode='multiple'
          allowClear
          showSearch
          optionFilterProp='label'
          maxTagCount='responsive'
          value={filters.systems}
          onChange={(systemIds) => update({ systems: systemIds })}
          placeholder='Sisteme'
          style={{ width: 200 }}
          options={systems.map((system) => ({ value: system.id, label: system.name }))}
        />
        <IconButton
          title={filters.overloadedOnly ? 'Toate persoanele' : 'Doar persoane cu depasiri'}
          icon={<WarningOutlined />}
          type={filters.overloadedOnly ? 'primary' : 'default'}
          danger={filters.overloadedOnly}
          onClick={() => update({ overloadedOnly: !filters.overloadedOnly })}
        />
        <IconButton
          title={filters.hideWeekends ? 'Afiseaza weekendul' : 'Ascunde weekendul'}
          icon={<CalendarOutlined />}
          type={filters.hideWeekends ? 'primary' : 'default'}
          onClick={() => update({ hideWeekends: !filters.hideWeekends })}
        />
        <IconButton
          title='Reseteaza filtrele'
          icon={<ClearOutlined />}
          disabled={!filtered}
          onClick={() => setFilters(EMPTY_FILTERS)}
        />
      </Flex>

      <Row gutter={[12, 12]}>
        {kpis.map((kpi) => (
          <Col key={kpi.title} xs={12} sm={8} xl={4}>
            <Card size='small'>
              <Statistic
                title={
                  <span>
                    {kpi.icon} {kpi.title}
                  </span>
                }
                value={kpi.value}
                suffix={kpi.suffix}
                valueStyle={{ color: kpi.color }}
              />
            </Card>
          </Col>
        ))}
        <Col xs={12} sm={8} xl={4}>
          <Card size='small'>
            <Statistic
              title={
                <span>
                  <DashboardOutlined /> Grad de utilizare
                </span>
              }
              value={utilization}
              suffix='%'
              valueStyle={{ color: utilization > 100 ? '#cf1322' : undefined }}
            />
            <Progress
              percent={Math.min(utilization, 100)}
              showInfo={false}
              size='small'
              status={utilizationStatus(utilization)}
            />
          </Card>
        </Col>
      </Row>

      {model.warnings.length > 0 && (
        <Alert
          type='warning'
          showIcon
          message={`${model.warnings.length} depasiri ale programului zilnic`}
          action={
            <Button type='primary' danger icon={<MedicineBoxOutlined />} onClick={() => setFixing(true)}>
              Rezolva
            </Button>
          }
          description={
            <Space wrap size={[4, 4]}>
              {model.warnings.slice(0, 12).map((load) => (
                <Tooltip key={`${load.userId}:${load.date}`} title='Filtreaza persoana'>
                  <Tag color='red' style={{ cursor: 'pointer' }} onClick={() => update({ people: [load.userId] })}>
                    {load.userName} · {dayjs(load.date).format('DD.MM')} · {hours(load.minutes)} /{' '}
                    {hours(load.capacity)}
                  </Tag>
                </Tooltip>
              ))}
              {model.warnings.length > 12 && (
                <Typography.Text type='secondary'>+{model.warnings.length - 12}</Typography.Text>
              )}
            </Space>
          }
        />
      )}
      {error && <Alert type='error' showIcon message={(error as Error).message} />}
      {!!data?.conflicts?.length && (
        <Alert
          type='warning'
          showIcon
          message={`${data.conflicts.length} suprapuneri de intervale orare (toate proiectele)`}
          description={
            <Space wrap>
              {data.conflicts.map((conflict, index) => (
                <Button
                  key={index}
                  size='small'
                  danger
                  onClick={() => openDay({ userId: conflict.userId, date: conflict.date })}
                >
                  {conflict.userName} · {dayjs(conflict.date).format('DD.MM')} · {timeText(conflict.startTime)}–
                  {timeText(conflict.endTime)} · {conflict.projectNumber} / {conflict.otherProjectNumber}
                </Button>
              ))}
            </Space>
          }
        />
      )}

      {view === 'table' && <PlanningTable {...viewProps} grid={data} />}
      {view === 'gantt' && (
        <GanttView {...viewProps} group={ganttGroup} split={split} filters={filters} projects={projects} />
      )}
      {view === 'load' && (
        <LoadView
          model={model}
          onProject={(projectId) => update({ projects: [projectId] })}
          onPerson={(userId) => update({ people: [userId] })}
          onDay={openDay}
        />
      )}
      <Typography.Text type='secondary' style={{ fontSize: 12 }}>
        Orele afisate respecta filtrele. Depasirile compara totalul zilnic al persoanei pe toate proiectele cu programul
        echipei sale de baza sau cu programul personalizat pentru acea zi.
      </Typography.Text>

      {day && data && (
        <TechnicianDayDrawer
          key={`${day.userId}:${day.date}`}
          target={day}
          grid={data}
          onClose={() => setDay(null)}
          onCreate={create}
          onEdit={(allocation) => setEditing({ allocation })}
        />
      )}
      {selected.length > 0 && (
        <SelectionDrawer
          key={details!.join()}
          allocations={selected}
          grid={data}
          onClose={() => setDetails(null)}
          onEdit={editDay}
        />
      )}
      {fixing && (
        <OverloadDrawer
          warnings={model.warnings}
          grid={data}
          onClose={() => setFixing(false)}
          onPerson={(userId) => update({ people: [userId] })}
        />
      )}
      {swap && (
        <TechnicianSwapModal userId={swap.userId} userName={swap.name} from={from} to={to} onClose={() => setSwap(null)} />
      )}
      {picking && (
        <PeoplePicker
          value={added}
          people={data?.people || []}
          teams={teams}
          date={from}
          onChange={setAdded}
          onClose={() => setPicking(false)}
        />
      )}
      {editing && (
        <AllocationModal
          key={editing.allocation?.id || JSON.stringify(editing.defaults || from)}
          allocation={editing.allocation}
          defaults={editing.defaults}
          from={from}
          to={to}
          onClose={() => setEditing(null)}
        />
      )}
    </Space>
  );
};
export default PlanningTab;
